import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { AdminMember } from '@grc/shared';
import { Field } from '../../components/Field';
import { ErrorLine, NoteLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { useMember } from '../../session';
import { listMembers } from './admin-api';
import { MemberTags } from './MemberTags';
import { byName, matches } from './search';

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; members: AdminMember[] };

/** What a removal leaves in the list's history entry: the name of whom it removed (Member.tsx). */
export type RemovedState = { removed: string };

function removedName(state: unknown): string | undefined {
  const removed = (state as Partial<RemovedState> | null)?.removed;
  return typeof removed === 'string' ? removed : undefined;
}

/**
 * The Members tab, an admin's (A5; D23, D25): every member by name, with their tags, a search
 * over the names and usernames, and the way to add one. A tap on a member opens their page.
 */
export function Members() {
  const location = useLocation();
  const navigate = useNavigate();
  // Said once: the history entry forgets it at once, so coming back here doesn't say it again.
  const [removed] = useState(() => removedName(location.state));
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (removed !== undefined) navigate(location.pathname, { replace: true, state: null });
  }, [removed, navigate, location.pathname]);

  useEffect(() => {
    let current = true;
    listMembers().then(
      (members) => {
        if (current) setLoad({ status: 'loaded', members: [...members].sort(byName) });
      },
      (error: unknown) => {
        if (current) setLoad({ status: 'failed', error });
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  function retry() {
    setLoad({ status: 'loading' });
    setAttempt((n) => n + 1);
  }

  return (
    <section className="screen">
      <div className="screen-head">
        <h1>{t('nav.members')}</h1>
        <Link to="/admin/members/new" className="button">
          {t('members.add')}
        </Link>
      </div>
      <NoteLine
        text={removed === undefined ? undefined : t('members.removed', { name: removed })}
      />
      {load.status === 'loading' && <p className="muted">{t('app.loading')}</p>}
      {load.status === 'failed' && (
        <div className="stack">
          <ErrorLine text={errorText(load.error)} />
          <button type="button" className="button" onClick={retry}>
            {t('app.retry')}
          </button>
        </div>
      )}
      {load.status === 'loaded' && <MemberList members={load.members} />}
    </section>
  );
}

/** The search, and the members it finds; the count while nothing is typed. */
function MemberList({ members }: { members: AdminMember[] }) {
  const me = useMember();
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const shown = members.filter((member) => matches(member, query));

  return (
    <>
      <Field
        type="search"
        label={t('members.search')}
        name="search"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {!searching && (
        <p className="muted small">
          {members.length === 1
            ? t('members.countOne')
            : t('members.count', { count: members.length })}
        </p>
      )}
      {searching && shown.length === 0 && (
        <p className="muted">{t('members.noMatch', { query: query.trim() })}</p>
      )}
      {shown.length > 0 && (
        <ul className="member-list">
          {shown.map((member) => (
            <li key={member.id} className="member-row">
              {/* The link's name is the member's; the whole row is its tap target (styles.css). */}
              <Link
                className="member-row-link"
                to={`/admin/members/${encodeURIComponent(member.id)}`}
              >
                {member.displayName}
              </Link>
              <span className="member-row-username">{member.username}</span>
              <MemberTags member={member} own={member.id === me.id} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
